import {
    addDays,
    differenceInMinutes,
    endOfDay,
    isSameDay,
    startOfDay,
    startOfWeek,
} from 'date-fns';

export type WeekPlanEvent = {
    id: string;
    start: Date;
    end: Date;
    allDay?: boolean;
};

/** One hour of the time grid. Offset and size are in hour units. */
export type TimeAxisRow = {
    startHour: number;
    endHour: number;
    offset: number;
    size: number;
};

export type TimeAxis = {
    rows: TimeAxisRow[];
    totalUnits: number;
};

export type PositionedWeekPlanEvent<TEvent extends WeekPlanEvent> = {
    event: TEvent;
    // percentages of the day column, so print can rescale the grid
    top: number;
    height: number;
    column: number;
    columns: number;
};

const WEEK_OPTIONS = { weekStartsOn: 1 } as const;
const DAYS_IN_WEEK = 7;
const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;

// the grid always covers a normal activity day, and grows to fit earlier or
// later events
const DEFAULT_HOURS = { startHour: 8, endHour: 18 };

export const getWeekDays = (weekDate: Date) => {
    const weekStart = startOfWeek(weekDate, WEEK_OPTIONS);

    return Array.from({ length: DAYS_IN_WEEK }, (_unused, dayOffset) =>
        addDays(weekStart, dayOffset),
    );
};

const getMinutesIntoDay = (date: Date, day: Date) =>
    differenceInMinutes(date, startOfDay(day));

// A timed event sits in the grid when it starts and ends on this day. An end at
// midnight counts as the end of the same day.
const isTimedOnDay = (event: WeekPlanEvent, day: Date) => {
    if (event.allDay || !isSameDay(event.start, day)) return false;

    const endMinutes = getMinutesIntoDay(event.end, day);
    return endMinutes <= MINUTES_IN_DAY;
};

const byStartThenLongest = (first: WeekPlanEvent, second: WeekPlanEvent) =>
    first.start.getTime() - second.start.getTime() ||
    second.end.getTime() - first.end.getTime();

// all-day events and events spanning several days repeat on every day they
// cover, in a row above the time grid
export const getAllDayEvents = <TEvent extends WeekPlanEvent>(
    events: TEvent[],
    day: Date,
): TEvent[] => {
    const dayStart = startOfDay(day);
    const dayEnd = endOfDay(day);

    return events
        .filter(
            event =>
                event.start <= dayEnd &&
                event.end >= dayStart &&
                !isTimedOnDay(event, day),
        )
        .sort(byStartThenLongest);
};

export const getTimedEvents = <TEvent extends WeekPlanEvent>(
    events: TEvent[],
    day: Date,
): TEvent[] =>
    events.filter(event => isTimedOnDay(event, day)).sort(byStartThenLongest);

export const getTimeAxis = (
    events: WeekPlanEvent[],
    days: Date[],
): TimeAxis => {
    let { startHour, endHour } = DEFAULT_HOURS;

    days.forEach(day => {
        getTimedEvents(events, day).forEach(event => {
            const startMinutes = getMinutesIntoDay(event.start, day);
            const endMinutes = getMinutesIntoDay(event.end, day);
            startHour = Math.min(
                startHour,
                Math.floor(startMinutes / MINUTES_IN_HOUR),
            );
            endHour = Math.max(
                endHour,
                Math.ceil(endMinutes / MINUTES_IN_HOUR),
            );
        });
    });

    const rows = Array.from(
        { length: endHour - startHour },
        (_unused, index): TimeAxisRow => ({
            startHour: startHour + index,
            endHour: startHour + index + 1,
            offset: index,
            size: 1,
        }),
    );

    return { rows, totalUnits: rows.length };
};

const getAxisPosition = (axis: TimeAxis, minutes: number) => {
    const axisStartMinutes = (axis.rows[0]?.startHour ?? 0) * MINUTES_IN_HOUR;
    const units = (minutes - axisStartMinutes) / MINUTES_IN_HOUR;
    return (units / axis.totalUnits) * 100;
};

// Overlapping events share the column width. Events that overlap each other,
// directly or through a chain, form one cluster, and every event in a cluster
// gets the same column count.
export const layoutDayEvents = <TEvent extends WeekPlanEvent>(
    events: TEvent[],
    day: Date,
    axis: TimeAxis,
): PositionedWeekPlanEvent<TEvent>[] => {
    const positioned: PositionedWeekPlanEvent<TEvent>[] = [];
    let cluster: PositionedWeekPlanEvent<TEvent>[] = [];
    let columnEnds: number[] = [];
    let clusterEnd = -Infinity;

    const closeCluster = () => {
        cluster.forEach(item => {
            item.columns = columnEnds.length;
        });
        positioned.push(...cluster);
        cluster = [];
        columnEnds = [];
    };

    getTimedEvents(events, day).forEach(event => {
        const start = event.start.getTime();
        const end = Math.max(event.end.getTime(), start);

        if (start >= clusterEnd) closeCluster();

        let column = columnEnds.findIndex(columnEnd => columnEnd <= start);
        if (column === -1) {
            column = columnEnds.length;
            columnEnds.push(end);
        } else {
            columnEnds[column] = end;
        }
        clusterEnd = Math.max(clusterEnd, end);

        const startMinutes = getMinutesIntoDay(event.start, day);
        const endMinutes = Math.max(
            getMinutesIntoDay(event.end, day),
            startMinutes,
        );

        const top = getAxisPosition(axis, startMinutes);

        cluster.push({
            event,
            top,
            height: getAxisPosition(axis, endMinutes) - top,
            column,
            columns: 1,
        });
    });
    closeCluster();

    return positioned;
};
